import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { round } from 'lodash'
import ReactECharts from 'echarts-for-react'
import { Card, Grid, Stack, Typography } from '@mui/material'
import useFetch from 'common/hooks/useFetch'
import useUpdateEffect from 'common/hooks/useUpdateEffect'
import { STATUS } from 'common/constants/storeConstants'
import { getDate } from 'common/utils/DateUtil'
import { formatMilliers } from 'common/utils/StringUtil'
import { CardTable, SORT } from 'common/components/datatable/Table'
import ApplicationConf from 'conf/ApplicationConf'
import { MONTH } from '../StatisticsUtil'

// Classement P3/P4/P5 par PRIORITÉ Mantis uniquement (la sévérité n'est plus utilisée) :
//   P3 = Urgente (50) / Immédiate (60), P4 = Élevée (40), P5 = Normale/Basse/Aucune (<= 30).
const LEVELS = ['P3', 'P4', 'P5']
const KPI_TARGET = { P3: 5, P4: 15, P5: 30 } // objectif de résolution en jours ouvrés

// Couleurs des GRAPHES (images 1 & 4) : P3/Documentation rouge, P4/Prestation bleu, P5/Assistance vert.
const CHART_COLORS = { P3: '#a52a2a', P4: '#2e75b6', P5: '#4a9c2d' }
// Couleurs de fond de la cellule "Priorité" dans la synthèse KPI (image 3) : or / bleu clair / vert.
const KPI_CELL_COLORS = { P3: '#ffc000', P4: '#9dc3e6', P5: '#70ad47', Total: '#d9d9d9' }

const priorityLevel = (priority) => {
    if (priority === 50 || priority === 60) {
        return 'P3'
    }
    if (priority === 40) {
        return 'P4'
    }
    return 'P5'
}

// Types de demandes affichés dans le graphe #6 (Analyse = "Documentation" côté métier).
const DEMANDE_TYPES = [
    { category: 'Analyse', label: 'Documentation', color: '#a52a2a' },
    { category: 'Prestation', label: 'Prestation', color: '#2e75b6' },
    { category: 'Assistance', label: 'Assistance', color: '#4a9c2d' },
]

const saveAsImage = (name) => ({
    show: true,
    right: 10,
    top: 8,
    feature: {
        saveAsImage: {
            show: true,
            title: 'Télécharger l\'image',
            name,
            backgroundColor: 'white',
            pixelRatio: 2,
        },
    },
})

// Liste des mois (moment au 1er du mois) couvrant [startSec, endSec].
const monthsBetween = (startSec, endSec) => {
    const end = moment.unix(endSec).startOf('month')
    const cur = moment.unix(startSec).startOf('month')
    const months = []
    while (!cur.isAfter(end)) {
        months.push(cur.clone())
        cur.add(1, 'month')
    }
    return months
}

// Ouvre le ticket Mantis dans un nouvel onglet SANS voler le focus (onglet d'arrière-plan) :
// window.open('_blank') basculerait sur l'onglet ; on simule donc un ctrl/cmd+clic sur un lien.
const openTicketInBackground = (id) => {
    const a = document.createElement('a')
    a.href = `https://support.aquasys.fr/view.php?id=${id}`
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    a.dispatchEvent(new window.MouseEvent('click', { ctrlKey: true, metaKey: true, bubbles: true }))
}

const monthKey = (m) => m.format('MM-YYYY')
const monthLabel = (m) => `${MONTH[m.month() + 1]} - ${m.format('YY')}`
const formatPct = (num, den) => `${(den ? (num / den) * 100 : 0).toFixed(2).replace('.', ',')} %`

const BarCard = ({ option, loading }) => (
    <Card>
        <ReactECharts option={option} notMerge lazyUpdate showLoading={loading} style={{ height: '320px' }} />
    </Card>
)

BarCard.propTypes = {
    option: PropTypes.object,
    loading: PropTypes.bool,
}

// Colonnes des deux tables de synthèse.
const KPI_COLS = [
    { key: 'level', label: 'Priorité', width: 84 },
    { key: 'cree', label: 'Créé', width: 60 },
    { key: 'kpi', label: 'KPI cible', width: 76 },
    { key: 'horsDelai', label: 'Hors délai', width: 78 },
    { key: 'taux', label: '%', width: 66 },
]
const DEMANDE_KPI_COLS = [
    { key: 'type', label: '', width: 118 },
    { key: 'cree', label: 'Créé', width: 70 },
    { key: 'horsDelai', label: 'Hors délai', width: 88 },
    { key: 'taux', label: '%', width: 84 },
]

// Table de synthèse rendue via ECharts (graphic) : reproduit l'affichage tableur (cellules colorées,
// "0,00 %", en-tête de période optionnel) et devient téléchargeable en image (toolbox saveAsImage).
// Chaque ligne peut porter un `cellColors` { colonne: fond } pour colorer certaines cellules.
const GraphicTable = ({ columns, rows, title, periodLabel, downloadName, loading }) => {
    const left = 12
    const rowH = 34
    const titleH = title ? 30 : 0
    const toolbarSpace = 34
    const bandH = periodLabel ? 30 : 0
    const tableTop = titleH + toolbarSpace + bandH
    const totalW = columns.reduce((acc, c) => acc + c.width, 0)

    const colX = []
    columns.reduce((x, c) => {
        colX.push(x)
        return x + c.width
    }, left)

    const graphic = []
    const addCell = (x, y, w, h, text, { fill = 'white', bold = false } = {}) => {
        graphic.push({ type: 'rect', shape: { x, y, width: w, height: h }, style: { fill, stroke: '#9e9e9e', lineWidth: 1 }, silent: true })
        if (text !== '' && text !== null && text !== undefined) {
            graphic.push({
                type: 'text',
                silent: true,
                style: { text: String(text), x: x + w / 2, y: y + h / 2, textAlign: 'center', textVerticalAlign: 'middle', fill: 'black', fontSize: 13, fontWeight: bold ? 'bold' : 'normal' },
            })
        }
    }

    // Titre de la table (inclus dans l'image téléchargée)
    if (title) {
        graphic.push({
            type: 'text',
            silent: true,
            style: { text: title, x: left + totalW / 2, y: titleH / 2 + 4, textAlign: 'center', textVerticalAlign: 'middle', fill: '#333', fontSize: 15, fontWeight: 'bold' },
        })
    }

    // Bandeau de période optionnel : vide au-dessus de la 1re colonne, libellé au-dessus des autres.
    if (periodLabel) {
        const bandY = titleH + toolbarSpace
        addCell(colX[0], bandY, columns[0].width, bandH, '')
        addCell(colX[1], bandY, totalW - columns[0].width, bandH, periodLabel, { fill: '#f2f2f2', bold: true })
    }

    // En-têtes de colonnes
    columns.forEach((c, i) => addCell(colX[i], tableTop, c.width, rowH, c.label, { fill: '#f2f2f2', bold: true }))

    // Lignes de données (cellules colorées via row.cellColors)
    rows.forEach((row, r) => {
        const y = tableTop + (r + 1) * rowH
        columns.forEach((c, i) => {
            const fill = row.cellColors?.[c.key] ?? 'white'
            addCell(colX[i], y, c.width, rowH, row[c.key], { fill, bold: fill !== 'white' })
        })
    })

    const width = left * 2 + totalW
    const height = tableTop + (rows.length + 1) * rowH + 10
    const option = { toolbox: saveAsImage(downloadName), graphic }

    return (
        <Card>
            <ReactECharts option={option} notMerge showLoading={loading} style={{ height: `${height}px`, width: `${width}px`, margin: '0 auto' }} />
        </Card>
    )
}

GraphicTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.object),
    rows: PropTypes.arrayOf(PropTypes.object),
    title: PropTypes.string,
    periodLabel: PropTypes.string,
    downloadName: PropTypes.string,
    loading: PropTypes.bool,
}

const IncidentsSupportApp = ({ filter }) => {
    const body = useMemo(() => ({
        startDate: filter.startDate,
        endDate: filter.endDate,
        projects: filter.projects,
    }), [filter])

    const { data = { anomalies: [], demandes: [] }, reload, status } = useFetch(ApplicationConf.support.statisticsIncidents(), { method: 'POST', body })
    const loading = status === STATUS.LOADING

    // Le filtre (projets / période) ne s'applique qu'au clic sur « Rechercher » : recharge sur changement de body.
    useUpdateEffect(() => {
        reload()
    }, [body])

    const anomalies = useMemo(() => data?.anomalies ?? [], [data])
    const demandes = useMemo(() => data?.demandes ?? [], [data])

    const months = useMemo(() => monthsBetween(filter.startDate, filter.endDate), [filter.startDate, filter.endDate])
    const monthKeys = useMemo(() => months.map(monthKey), [months])
    const monthLabels = useMemo(() => months.map(monthLabel), [months])

    const periodLabel = useMemo(() => {
        const y1 = moment.unix(filter.startDate).format('YYYY')
        const y2 = moment.unix(filter.endDate).format('YYYY')
        return y1 === y2 ? y1 : `${y1}-${y2}`
    }, [filter.startDate, filter.endDate])

    // Anomalies enrichies de leur niveau (P3/P4/P5) et de leur clé de mois de création.
    const enriched = useMemo(() => anomalies.map(a => ({
        ...a,
        level: priorityLevel(a.priority),
        mKey: monthKey(moment.unix(a.dateSubmitted)),
    })), [anomalies])

    // #1 Graphe anomalies / mois par priorité (barres empilées)
    const incidentsOption = useMemo(() => {
        const counts = {}
        enriched.forEach(a => {
            counts[a.mKey] = counts[a.mKey] || { P3: 0, P4: 0, P5: 0 }
            counts[a.mKey][a.level] = counts[a.mKey][a.level] + 1
        })
        return {
            title: { text: 'Nombre d\'anomalies par priorité et par mois', left: 'center', top: 10 },
            grid: { top: 50, bottom: 60, containLabel: true },
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            toolbox: saveAsImage('Anomalies_par_priorite'),
            legend: { bottom: '2%', left: 'center' },
            xAxis: { type: 'category', data: monthLabels, axisLabel: { interval: 0, rotate: 40 } },
            yAxis: { type: 'value', minInterval: 1, axisLabel: { formatter: v => formatMilliers(v) } },
            series: LEVELS.map(l => ({
                name: l,
                type: 'bar',
                stack: 'total',
                itemStyle: { color: CHART_COLORS[l] },
                data: monthKeys.map(k => counts[k]?.[l] ?? 0),
            })),
        }
    }, [enriched, monthKeys, monthLabels])

    // #6 Graphe demandes ouvertes / mois par type
    const demandesOption = useMemo(() => {
        const counts = {}
        demandes.forEach(d => {
            const key = monthKey(moment.unix(d.dateSubmitted))
            counts[key] = counts[key] || {}
            counts[key][d.category] = (counts[key][d.category] || 0) + 1
        })
        return {
            title: { text: 'Nombre de demandes ouvertes par type et par mois', left: 'center', top: 10 },
            grid: { top: 50, bottom: 60, containLabel: true },
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            toolbox: saveAsImage('Demandes_par_type'),
            legend: { bottom: '2%', left: 'center' },
            xAxis: { type: 'category', data: monthLabels, axisLabel: { interval: 0, rotate: 40 } },
            yAxis: { type: 'value', minInterval: 1, axisLabel: { formatter: v => formatMilliers(v) } },
            series: DEMANDE_TYPES.map(t => ({
                name: t.label,
                type: 'bar',
                stack: 'total',
                itemStyle: { color: t.color },
                data: monthKeys.map(k => counts[k]?.[t.category] ?? 0),
            })),
        }
    }, [demandes, monthKeys, monthLabels])

    // Ligne de tableau détail "hors délai" (délai résolution > KPI du niveau).
    const buildDetailRow = (a, resolveDate, delay) => ({
        id: a.id,
        ticketId: { value: a.id },
        summary: { value: a.summary },
        project: { value: a.project },
        priorite: { value: a.level },
        created: { value: getDate(a.dateSubmitted * 1000), sortValue: a.dateSubmitted },
        resolved: { value: resolveDate ? getDate(resolveDate * 1000) : '', sortValue: resolveDate ?? 0 },
        delay: { value: Number.isFinite(delay) ? round(delay, 2) : '', sortValue: delay ?? 0 },
        kpi: { value: KPI_TARGET[a.level] },
    })

    const detailHeaders = [
        { key: 'ticketId', value: 'Id' },
        { key: 'summary', value: 'Titre' },
        { key: 'project', value: 'Projet' },
        { key: 'priorite', value: 'Priorité' },
        { key: 'created', value: 'Créé le' },
        { key: 'resolved', value: 'Résolu le' },
        { key: 'delay', value: 'Délai (j ouvrés)' },
        { key: 'kpi', value: 'KPI (j)' },
    ]

    // #2 Hors délai sur le dernier passage en Attente validation interne (statut 60)
    const outOfDelayAvi = useMemo(
        () => enriched
            .filter(a => Number.isFinite(a.delayAvi) && a.delayAvi > KPI_TARGET[a.level])
            .map(a => buildDetailRow(a, a.lastAttenteValidation, a.delayAvi)),
        [enriched],
    )

    // #3 Hors délai sur le dernier passage En recette (statut 85)
    const outOfDelayRecette = useMemo(
        () => enriched
            .filter(a => Number.isFinite(a.delayRecette) && a.delayRecette > KPI_TARGET[a.level])
            .map(a => buildDetailRow(a, a.lastRecette, a.delayRecette)),
        [enriched],
    )

    // #4 Synthèse par priorité (base : En recette). Hors délai = résolu en recette ET délai > KPI.
    const kpiRows = useMemo(() => {
        const isLate = a => Number.isFinite(a.delayRecette) && a.delayRecette > KPI_TARGET[a.level]
        return LEVELS.map(level => {
            const ofLevel = enriched.filter(a => a.level === level)
            const horsDelai = ofLevel.filter(isLate).length
            return { level, cree: ofLevel.length, kpi: KPI_TARGET[level], horsDelai, taux: formatPct(horsDelai, ofLevel.length), cellColors: { level: KPI_CELL_COLORS[level] } }
        })
    }, [enriched])

    // #4bis Synthèse KPI des demandes par type (Assistance / Prestation / Documentation = Analyse).
    // Pas de délai suivi pour les demandes (données sans date de résolution) : hors délai = 0.
    const demandeKpiRows = useMemo(() => ([
        { category: 'Assistance', label: 'Assistance' },
        { category: 'Prestation', label: 'Prestation' },
        { category: 'Analyse', label: 'Documentation' },
    ].map(t => {
        const cree = demandes.filter(d => d.category === t.category).length
        return cree === 0
            ? { type: t.label, cree: '', horsDelai: '', taux: '' }
            : { type: t.label, cree, horsDelai: 0, taux: formatPct(0, cree) }
    })), [demandes])

    // #5 Toutes les anomalies de la période
    const allRows = useMemo(() => enriched.map(a => ({
        id: a.id,
        ticketId: { value: a.id },
        summary: { value: a.summary },
        project: { value: a.project },
        priorite: { value: a.level },
        created: { value: getDate(a.dateSubmitted * 1000), sortValue: a.dateSubmitted },
        avi: { value: a.lastAttenteValidation ? getDate(a.lastAttenteValidation * 1000) : '', sortValue: a.lastAttenteValidation ?? 0 },
        recette: { value: a.lastRecette ? getDate(a.lastRecette * 1000) : '', sortValue: a.lastRecette ?? 0 },
        delayRecette: { value: Number.isFinite(a.delayRecette) ? round(a.delayRecette, 2) : '', sortValue: a.delayRecette ?? 0 },
    })), [enriched])

    const allHeaders = [
        { key: 'ticketId', value: 'Id' },
        { key: 'summary', value: 'Titre' },
        { key: 'project', value: 'Projet' },
        { key: 'priorite', value: 'Priorité' },
        { key: 'created', value: 'Créé le' },
        { key: 'avi', value: 'Attente validation le' },
        { key: 'recette', value: 'En recette le' },
        { key: 'delayRecette', value: 'Délai recette (j ouvrés)' },
    ]

    return (
        <Stack margin={1} spacing={2}>
            {/* ------- Section Anomalies ------- */}
            <Typography variant='h5' sx={{ fontWeight: 'bold', borderBottom: '2px solid #161832', paddingBottom: 0.5 }}>{'Anomalies'}</Typography>
            <Grid container spacing={2} alignItems='flex-start'>
                <Grid size={6}>
                    <BarCard option={incidentsOption} loading={loading} />
                </Grid>
                <Grid size={6}>
                    <GraphicTable columns={KPI_COLS} rows={kpiRows} title='Synthèse KPI anomalies' periodLabel={periodLabel} downloadName='Synthese_KPI_anomalies' loading={loading} />
                </Grid>
            </Grid>
            <Grid container spacing={2} alignItems='flex-start'>
                <Grid size={6}>
                    <CardTable
                        title='Anomalies hors délai — résolu (Attente validation interne)'
                        color='#c0392b'
                        rows={outOfDelayAvi}
                        headers={detailHeaders}
                        rowsPerPageDefault={25}
                        status={status}
                        defaultSort={{ column: 'delay', direction: SORT.DESC }}
                        onClickRow={({ id }) => openTicketInBackground(id)}
                    />
                </Grid>
                <Grid size={6}>
                    <CardTable
                        title='Anomalies hors délai — résolu (En recette)'
                        color='#c0392b'
                        rows={outOfDelayRecette}
                        headers={detailHeaders}
                        rowsPerPageDefault={25}
                        status={status}
                        defaultSort={{ column: 'delay', direction: SORT.DESC }}
                        onClickRow={({ id }) => openTicketInBackground(id)}
                    />
                </Grid>
            </Grid>
            <CardTable
                title='Toutes les anomalies de la période'
                color='#161832'
                rows={allRows}
                headers={allHeaders}
                rowsPerPageDefault={25}
                status={status}
                defaultSort={{ column: 'created', direction: SORT.DESC }}
                onClickRow={({ id }) => openTicketInBackground(id)}
            />

            {/* ------- Section Assistance ------- */}
            <Typography variant='h5' sx={{ fontWeight: 'bold', borderBottom: '2px solid #161832', paddingBottom: 0.5, marginTop: 2 }}>{'Assistance'}</Typography>
            <Grid container spacing={2} alignItems='flex-start'>
                <Grid size={6}>
                    <BarCard option={demandesOption} loading={loading} />
                </Grid>
                <Grid size={6}>
                    <GraphicTable columns={DEMANDE_KPI_COLS} rows={demandeKpiRows} title='Synthèse KPI assistance' downloadName='Synthese_KPI_assistance' loading={loading} />
                </Grid>
            </Grid>
        </Stack>
    )
}

IncidentsSupportApp.propTypes = {
    filter: PropTypes.shape({
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        projects: PropTypes.arrayOf(PropTypes.string),
        categories: PropTypes.arrayOf(PropTypes.string),
    }),
}

export default IncidentsSupportApp
